import produce from 'immer';
import { useCallback, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  FormHandlerRef,
  FormHandlerState,
  createFormCoordinatorSubject,
} from './FormCoordinatorSubject';

export type IUseFormCoordinator = ReturnType<typeof useFormCoordinator>;

/**
 * Punto centrale per la gestione del coordinamento dei form.
 * Registra l'insieme dei form e per ognuno gestisce:
 *  - Una `ref` ai metodi di `react-hook-form`, in modo che possano essere usati
 *    da altri componenti.
 *  - Lo stato del form, in modo che possa essere utilizzato dal parent
 */
export function useFormCoordinator() {
  /**
   * Subject utilizzato per comunicare con i form registrati.
   * Al momento non viene utilizzato, ma potrebbe essere utilizzato per
   * la gestione di eventi di invalidazione, invio, ecc.
   */
  const subject = useRef(createFormCoordinatorSubject());

  /**
   * Riferimenti ai metodi.
   */
  const formRefs = useRef(new Map<string, FormHandlerRef>());

  /**
   * Stato dei form registrati, ad esempio se sono dirty o meno.
   */
  const [forms, setForms] = useState<Record<string, FormHandlerState>>({});

  const registerForm = useCallback(
    (ref: React.MutableRefObject<UseFormReturn>, form: FormHandlerState) => {
      formRefs.current.set(form.id, { id: form.id, ref });
      setForms(
        produce(draft => {
          draft[form.id] = form;
        }),
      );
    },
    [setForms],
  );

  const unregisterForm = useCallback(
    (id: string) => {
      formRefs.current.delete(id);
      setForms(
        produce(draft => {
          delete draft[id];
        }),
      );
    },
    [setForms],
  );

  const watchFormValues = useCallback(
    (formId: string | null | undefined) => {
      forms; // NOSONAR
      if (!formId) return null;
      const form = formRefs.current.get(formId);
      if (!form) return null;
      return form.ref.current.watch;
    },
    [forms],
  );

  return {
    forms,
    subject: subject.current,
    formRefs,
    registerForm,
    unregisterForm,
    watchFormValues,
  };
}
